import every from 'lodash/every';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import {
  cryptoHelper,
  CUSTOM_NOTE_CONTEXT,
  IBakery,
  IProductType,
  IUpsellProductType,
  PRODUCT_TYPE_STRUCTURE_TYPES,
  MODAL
} from '@cakeiteasy/web-utils';
import { Observable, of } from 'rxjs';
import { ComponentFactoryResolver, Directive, OnDestroy, Optional, signal } from '@angular/core';
import { ModalOpenerService } from '../../modals/modal-opener.service';
import { ContextProviderService } from '../services/context-provider.service';
import { Store, select } from '@ngrx/store';
import { ProductsApiService } from '../services/products-api.service';
import { map, take } from 'rxjs/operators';

import {
  CartServiceV2,
  CartState, DeliveryApiService,
  cartHelper,
  getCart,
  getCartBakery,
  getCartIsEmpty, getCartLines,
  ICakeCartLine,
  ICart,
  ICartLine
} from '@cakeiteasy/order-process';
import { EditingCakeSessionService, IEditingCakeSession } from '../services/editing-cake-session.service';
import { getEmptyCartLine } from '@cakeiteasy/order-process';
import { makeCartLineForStoreFromBuilder } from '@cakeiteasy/order-process';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ISubmitBuilderPayload } from '@cakeiteasy/order-process/src/cake-builder/interfaces';
import { DeliveryCheckerBaseComponent } from '@shared/base-classes/delivery-checker-base.component';
import { AnalyticTracker } from '@analytic/services/analytic-tracker.service';

@Directive({ standalone: false })
export abstract class BuildCakeBaseComponent extends DeliveryCheckerBaseComponent implements OnDestroy {
  public selectedCake: any;
  public cartLine$: Observable<ICakeCartLine>;
  public cartLines$: Observable<ICakeCartLine[]>;
  public upsellCartLines$: Observable<ICakeCartLine[]>;
  public lineSaving: Promise<any>;
  public upsellProductTypes = signal<IUpsellProductType[]>([]);

  public currentOrderLineBuilderId: string = null;
  public editingSession: IEditingCakeSession;
  public cartIsEmpty$: Observable<boolean>;

  public CUSTOM_NOTE_CONTEXT = CUSTOM_NOTE_CONTEXT.MARKETPLACE;

  protected constructor(protected _deliveryApi: DeliveryApiService,
                        protected _componentResolver: ComponentFactoryResolver,
                        protected _modalOpener: ModalOpenerService,
                        protected _store: Store<CartState>,
                        protected _cartService: CartServiceV2,
                        protected _productsApiService: ProductsApiService,
                        protected _toastr: ToastrService,
                        protected _translate: TranslateService,
                        protected _analyticTracker: AnalyticTracker,
                        @Optional() protected _cakeEditingSessionService: EditingCakeSessionService,
                        @Optional() protected _context: ContextProviderService) {
    super(_deliveryApi, _modalOpener, _componentResolver);

    this.cartIsEmpty$ = this._store.pipe(select(getCartIsEmpty));
  }

  public ngOnDestroy() {
    super.ngOnDestroy();

    if (this._cakeEditingSessionService) {
      this._cakeEditingSessionService.unregisterAll();
    }
  }

  public updateEditingSession(event: ICartLine) {
    if (this._cakeEditingSessionService && this.editingSession) {
      this._cakeEditingSessionService.updateSession(this.editingSession, event);
    }
  }

  public async loadCake(
    typeId: number,
    registerSession = true,
    orderLineBuilderId?: string
  ): Promise<IProductType> {
    this.editingSession = this._cakeEditingSessionService
      .getActiveSessionBy(typeId, orderLineBuilderId);

    this.currentOrderLineBuilderId = orderLineBuilderId ?? null;

    const productType$ = this._productsApiService.getProductType(typeId);
    const upsellProducts$ = this._productsApiService.getProductTypeUpsellProducts(typeId);
    const [productType, upsellProducts] = await Promise.all([productType$, upsellProducts$]);
    const params = productType.pieces ? { count: 0 } : {};

    // TODO remove if mail delivery become available NS3-3070
    productType.mailDeliveryAvailable = false;

    this.selectedCake = productType;
    this.cartLine$ = this._getCartLineObserver(productType, orderLineBuilderId, registerSession, params);
    this.cartLines$ = productType.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU
      ? of([]) : this._getMultiVariantsCartLinesObserver(orderLineBuilderId);
    this.upsellCartLines$ = this._getUpsellCartLinesObserver(upsellProducts.map(p => p.id));

    this.upsellProductTypes.set(upsellProducts);
    return this.selectedCake;
  }

  public showBasketResetWarning() {
    return this._modalOpener.showBasketResetWarning(this._componentResolver)
      .then(({ cleanCart, cancel }) => {
        if (cleanCart) {
          return this._cartService.resetCart()
            .then((cart) => {
              console.log('cart has been reset');
            });
        }

        return Promise.reject(`${MODAL.CLOSE_EVENTS.DISMISSED}: User cancelled Cart Resetting`);
      });
  }

  public async saveCartLine(event: { line: ICakeCartLine, productType?: IProductType | IUpsellProductType }) {
    const { line, productType = this.selectedCake } = event;
    const { bakery: productTypeBakery } = this.selectedCake;
    const cartBakery = await this._getCartBakery();

    if (line.id && !line.count) {
      return this._cartService.removeCartLine(line.clientId);
    }

    if (!cartBakery) {
      this._cartService.saveBakeryToStoreIfNecessary(productTypeBakery);
    } else if (productTypeBakery.id !== cartBakery.id) {
      try {
        let { cleanCart, cancel } = await this._modalOpener.showBasketResetWarning(this._componentResolver);

        if (cleanCart) {
          await (this.lineSaving = this._cartService.resetCart()
            .then(() => this._cartService.saveBakeryToStoreIfNecessary(productTypeBakery)));
        }

        if (cancel) {
          throw new Error('CANCELLED');
        }
      } catch (e) {
        return this.upsellCartLines$ = of([]);
      }
    }

    if (line.clientId) {
      line.clientId = await this._getCheckedClientId(line.clientId);
    }

    const savingCartLine = line.clientId ? line : makeCartLineForStoreFromBuilder(line, productType);
    this._cartService.putCartLineToStore(savingCartLine);
    this.upsellCartLines$ = this._getUpsellCartLinesObserver(this.upsellProductTypes().map(p => p.id));

    const { lines } = await this._cartService.putInQueueProductSaving(savingCartLine);
    const linesByClientId = keyBy(lines, 'clientId');
    this.upsellCartLines$ = this.upsellCartLines$
      .pipe(map(upsellLines => upsellLines.map(l => ({ ...l, ...linesByClientId[l.clientId] }))));
  }

  public async save(event: ISubmitBuilderPayload) {
    let savingLines = this._getSavingLines(event);
    const isEmptyCart = await this._getCartIsEmpty();

    if (isEmptyCart) {
      savingLines = savingLines
        .map((l) => ({ ...omit(l, 'id'), clientId: cryptoHelper.cartClientId() }));
    }

    if (!savingLines.length) {
      return Promise.resolve(null);
    } else if (savingLines.length === 1) {
      return this._saveProduct(savingLines[0]);
    } else {
      return this._saveProducts(savingLines);
    }
  }

  public resetCart(): Promise<any> {
    return this._cartService.resetCart();
  }

  public cakeSavingError() {
    this._toastr
      .error(this._translate.instant('notifications.error.cakeSaving.message'), '', { disableTimeOut: true });
  }

  public abstract onCartBusy();

  public trackAddToCart(event: ISubmitBuilderPayload) {
    const { line, lines = [], upsellLines = [], applyOnceLines = [] } = event;
    const bakery = this.selectedCake && this.selectedCake.bakery;
    const categoryName = this.selectedCake.category && this.selectedCake.category.name;

    return this._analyticTracker.trackAddToCart({
      bakery,
      categoryName,
      line: line,
      lines: [...lines, ...applyOnceLines],
      upsellLines: upsellLines,
    });
  }

  private _getSavingLines(event: {
    line?: ICakeCartLine,
    lines?: ICakeCartLine[],
    upsellLines?: ICakeCartLine[],
    applyOnceLines?: ICakeCartLine[]
  }) {
    const { line, lines, upsellLines, applyOnceLines } = event;

    // Builder id must be unified across sibling PIECES_GROUP lines before prepareCartLines() runs each one
    // through makeCartLineForStoreFromBuilder() below, since that mints its own random id per line otherwise
    // and _ensureSavingLinesBuilderId() would then see every line as already having one and leave them split.
    const rawProductLines = [line, ...(lines || [])].filter((l): l is ICakeCartLine => Boolean(l));
    const linesWithBuilderId = this._ensureSavingLinesBuilderId(rawProductLines);
    const lineWithBuilderId = line ? linesWithBuilderId[0] : null;
    const groupLinesWithBuilderId = line ? linesWithBuilderId.slice(1) : linesWithBuilderId;

    const productLines = [
      lineWithBuilderId,
      ...this.prepareCartLines(groupLinesWithBuilderId),
    ].filter((l): l is ICakeCartLine => Boolean(l));

    return [
      ...productLines,
      ...(applyOnceLines || []),
      ...(upsellLines || []),
    ].filter((l): l is ICakeCartLine => Boolean(l));
  }

  private prepareCartLines(lines: ICakeCartLine[]): ICakeCartLine[] {
    return lines
      .filter((l) => !l.id && l.count || l.id)
      .map(l => this.selectedCake?.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU
        ? l
        : { ...makeCartLineForStoreFromBuilder(l, this.selectedCake), ...l });
  }

  private async _saveProduct(line: ICakeCartLine): Promise<ICart> {
    let promise = Promise.resolve();
    const bakery = await this._getCartBakery();

    if (bakery && this.selectedCake.bakery.id !== bakery.id) {
      promise = this.showBasketResetWarning();
    }

    if (!line.id && !line.count) {
      return Promise.resolve(null as ICart);
    }

    if (line.id && !line.count) {
      return this.lineSaving = promise
        .then(() => this._cartService.removeCartLine(line.clientId));
    }

    return this.lineSaving = promise
      .then(() => {
        return this._cartService.saveProducts([line])
          .then(() => this._cartService.fetchCart())
          .catch((err) => {
            return Promise.reject(err);
          });
      })
      .catch((err) => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  private async _saveProducts(lines: ICakeCartLine[]) {
    if (every(lines, (l) => !l.count)) {
      if (this.selectedCake?.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU) {
        return this.lineSaving = this._cartService.removeProductType(lines[0]);
      }
      return this.lineSaving = this._cartService.resetCart();
    }

    const bakery = await this._getCartBakery();

    if (bakery && this.selectedCake.bakery.id !== bakery.id) {
      await this.showBasketResetWarning();
    }

    const removingLines = lines.filter(l => !l.count);
    const savingLines = lines.filter(l => l.count);

    const removingProducts$ = removingLines.map(l => this._cartService.removeCartLine(l.clientId));
    const saveProducts$ = this._cartService.saveProducts(savingLines)
      .then(() => this._cartService.fetchCart());

    return this.lineSaving = Promise.all([saveProducts$, ...removingProducts$])
      .then(([cart]) => cart);
  }

  private _getMultiVariantsCartLinesObserver(orderLineBuilderId?: string): Observable<ICakeCartLine[]> {
    return this._store.select(getCart)
      .pipe(
        map((cart) => {
          if (!cart || !orderLineBuilderId) {
            return [];
          }

          return cart.lines.filter(({ orderLineBuilderId: lineBuilderId }) => lineBuilderId === orderLineBuilderId);
        }),
        take(1)
      );
  }

  private _getUpsellCartLinesObserver(ids: number[]) {
    if (this.selectedCake?.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU) {
      return this._getMultiVariantsCartLinesObserver(this.currentOrderLineBuilderId);
    }

    return this._store.select(getCart)
      .pipe(
        map((cart) => {
          return cart ? cart.lines.filter(({ productType, orderLineBuilderId }) => productType
            && !orderLineBuilderId && ids.indexOf(productType.id) >= 0) : [];
        }),
        take(1)
      );
  }

  private _getCartLineObserver(
    productType,
    orderLineBuilderId: string,
    registerSession,
    params = {}
  ): Observable<ICakeCartLine> {
    return this._store.select(getCart)
      .pipe(
        map((cart) => {
          let line = null;

          if (cart) {
            line = orderLineBuilderId && productType.structureType !== PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU
              ? cart.lines.find((line) => line.orderLineBuilderId === orderLineBuilderId)
              : null;

            if (this.editingSession && this.editingSession.data) {
              line = this.editingSession.data;
            } else if (this._cakeEditingSessionService && registerSession) {
              this.editingSession = this._cakeEditingSessionService.register(productType.id, orderLineBuilderId);
            }
          }

          if (!line) {
            return { ...getEmptyCartLine(productType, null, params) };
          }

          return line;
        }),
        take(1)
      );
  }

  private _getCartBakery(): Promise<IBakery> {
    return new Promise(resolve => {
      this._store.select(getCartBakery)
        .pipe(take(1))
        .subscribe(bakery => resolve(bakery));
    });
  }

  private async _getCheckedClientId(clientId: string): Promise<string> {
    return new Promise(resolve => {
      this._store.pipe(select(getCartLines))
        .pipe(take(1))
        .subscribe(lines => {
          const line = lines.find(l => l.clientId === clientId);
          const validClientId = line && line.clientId || cryptoHelper.cartClientId();
          resolve(validClientId);
        });
    });
  }

  private async _getCartIsEmpty(): Promise<boolean> {
    return new Promise(res => {
      this.cartIsEmpty$.pipe(take(1)).subscribe(cartIsEmpty => res(cartIsEmpty));
    });
  }

  private _ensureSavingLinesBuilderId(lines: ICakeCartLine[]): ICakeCartLine[] {
    const existingBuilderId = this.currentOrderLineBuilderId ||
      lines.find(l => l.orderLineBuilderId)?.orderLineBuilderId;
    const builderId = existingBuilderId || cryptoHelper.cartClientId();

    return lines.map((line) => {
      if (!line.orderLineBuilderId && cartHelper.canHaveOrderLineBuilderId(line, this.selectedCake)) {
        return { ...line, orderLineBuilderId: builderId };
      }

      return line;
    });
  }
}
